import { useEffect, useLayoutEffect, useState, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import { ArrowLeft, ArrowRight, Sparkles, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useTourStore } from '@/stores';
import { tourSteps, type TourStep } from './tourSteps';

const POPOVER_GAP = 28;
const POPOVER_WIDTH = 340;
/** Mobile-friendly bottom-sheet layout kicks in below this breakpoint (Tailwind `lg`). */
const MOBILE_BREAKPOINT = 1024;
const VIEWPORT_PADDING = 16;
const POPOVER_HEIGHT_FALLBACK = 180;
/** Min top so the popover never hides under the sticky TopBar (~56px) + breathing room. */
const TOP_SAFE_AREA = 80;
/** Distance from the bottom of the viewport for the mobile bottom-sheet popover. */
const MOBILE_BOTTOM_OFFSET = 16;
/** Arrow long-axis (width for top/bottom, height for left/right). */
const ARROW_LONG = 20;
/** Arrow short-axis (length the tail protrudes from the popover edge). */
const ARROW_SHORT = 11;
/** Padding around the target inside the spotlight cutout. */
const SPOTLIGHT_PAD = 4;

function useViewport(): { isMobile: boolean } {
  const [width, setWidth] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth : 1024
  );
  useEffect(() => {
    const onResize = () => setWidth(window.innerWidth);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);
  return { isMobile: width < MOBILE_BREAKPOINT };
}

interface TargetRect {
  top: number;
  left: number;
  width: number;
  height: number;
}

function useTargetRect(targetSelector: string | undefined): TargetRect | null {
  const [rect, setRect] = useState<TargetRect | null>(null);

  useLayoutEffect(() => {
    if (!targetSelector) {
      setRect(null);
      return;
    }

    const measure = () => {
      const el = document.querySelector(targetSelector);
      if (!el) {
        setRect(null);
        return;
      }
      const r = el.getBoundingClientRect();
      setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
    };

    // Bring the target into view first — it may be inside a scrollable
    // sidebar/nav that hasn't scrolled to it yet. Then re-measure after the
    // scroll animation has had a chance to settle.
    const el = document.querySelector(targetSelector);
    if (el) {
      (el as HTMLElement).scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    measure();
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);
    // ResizeObserver catches layout changes that don't fire resize/scroll —
    // e.g. when the user collapses the sidebar, the target's box changes
    // size and position but no scroll/resize event is dispatched.
    let ro: ResizeObserver | null = null;
    if (el && typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(measure);
      ro.observe(el);
      if (el.parentElement) ro.observe(el.parentElement);
      ro.observe(document.documentElement);
    }
    // Track the target's position during the CSS transition that follows a
    // sidebar collapse/expand (Tailwind's transition-all default is 150ms,
    // the sidebar uses duration-300).
    const transitionFrames: number[] = [];
    for (const delay of [50, 120, 200, 320, 450]) {
      transitionFrames.push(window.setTimeout(measure, delay));
    }
    return () => {
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
      transitionFrames.forEach(window.clearTimeout);
      if (ro) ro.disconnect();
    };
  }, [targetSelector]);

  return rect;
}

type ResolvedPlacement = 'top' | 'right' | 'bottom' | 'left';

interface PopoverPosition {
  top: number;
  left: number;
  centered: boolean;
  placement: ResolvedPlacement | null;
}

function getPopoverPosition(
  step: TourStep,
  rect: TargetRect | null,
  popoverHeight: number
): PopoverPosition {
  if (!rect || step.placement === 'center' || !step.targetSelector) {
    return { top: 0, left: 0, centered: true, placement: null };
  }
  const placement: ResolvedPlacement = (step.placement ?? 'right') as ResolvedPlacement;
  const vw = window.innerWidth;
  const vh = window.innerHeight;

  let top: number;
  let left: number;

  switch (placement) {
    case 'right':
      top = rect.top + rect.height / 2 - popoverHeight / 2;
      left = rect.left + rect.width + POPOVER_GAP;
      break;
    case 'left':
      top = rect.top + rect.height / 2 - popoverHeight / 2;
      left = rect.left - POPOVER_WIDTH - POPOVER_GAP;
      break;
    case 'top':
      top = rect.top - popoverHeight - POPOVER_GAP;
      left = rect.left + rect.width / 2 - POPOVER_WIDTH / 2;
      break;
    case 'bottom':
      top = rect.top + rect.height + POPOVER_GAP;
      left = rect.left + rect.width / 2 - POPOVER_WIDTH / 2;
      break;
    default:
      top = rect.top;
      left = rect.left;
  }

  // Clamp to viewport using the popover's actual size on both axes.
  // Floor on top is TOP_SAFE_AREA so the popover stays clear of the sticky TopBar.
  left = Math.max(VIEWPORT_PADDING, Math.min(left, vw - POPOVER_WIDTH - VIEWPORT_PADDING));
  top = Math.max(TOP_SAFE_AREA, Math.min(top, vh - popoverHeight - VIEWPORT_PADDING));

  return { top, left, centered: false, placement };
}

interface ArrowGeometry {
  style: React.CSSProperties;
  clipPath: string;
  width: number;
  height: number;
}

/**
 * A real speech-bubble tail: triangle with one edge flush against the
 * popover and the opposite vertex pointing at the target's center.
 * Returns the geometry needed to render it.
 */
function getArrowGeometry(
  placement: ResolvedPlacement,
  rect: TargetRect,
  pos: { top: number; left: number },
  popoverHeight: number
): ArrowGeometry {
  const edgePad = 20;
  const targetCenterX = rect.left + rect.width / 2;
  const targetCenterY = rect.top + rect.height / 2;

  switch (placement) {
    case 'right': {
      // Popover on right of target; tail on popover's LEFT edge pointing LEFT.
      const raw = targetCenterY - pos.top - ARROW_LONG / 2;
      const top = Math.max(edgePad, Math.min(raw, popoverHeight - edgePad - ARROW_LONG));
      return {
        clipPath: 'polygon(100% 0, 100% 100%, 0 50%)',
        width: ARROW_SHORT,
        height: ARROW_LONG,
        style: { top, left: -ARROW_SHORT },
      };
    }
    case 'left': {
      // Popover on left; tail on RIGHT edge pointing RIGHT.
      const raw = targetCenterY - pos.top - ARROW_LONG / 2;
      const top = Math.max(edgePad, Math.min(raw, popoverHeight - edgePad - ARROW_LONG));
      return {
        clipPath: 'polygon(0 0, 100% 50%, 0 100%)',
        width: ARROW_SHORT,
        height: ARROW_LONG,
        style: { top, right: -ARROW_SHORT },
      };
    }
    case 'top': {
      // Popover above target; tail on BOTTOM edge pointing DOWN.
      const raw = targetCenterX - pos.left - ARROW_LONG / 2;
      const left = Math.max(edgePad, Math.min(raw, POPOVER_WIDTH - edgePad - ARROW_LONG));
      return {
        clipPath: 'polygon(0 0, 100% 0, 50% 100%)',
        width: ARROW_LONG,
        height: ARROW_SHORT,
        style: { bottom: -ARROW_SHORT, left },
      };
    }
    case 'bottom':
    default: {
      // Popover below target; tail on TOP edge pointing UP.
      const raw = targetCenterX - pos.left - ARROW_LONG / 2;
      const left = Math.max(edgePad, Math.min(raw, POPOVER_WIDTH - edgePad - ARROW_LONG));
      return {
        clipPath: 'polygon(50% 0, 100% 100%, 0 100%)',
        width: ARROW_LONG,
        height: ARROW_SHORT,
        style: { top: -ARROW_SHORT, left },
      };
    }
  }
}

const TOUR_KEYFRAMES = `
@keyframes lili-tour-pulse {
  0%, 100% {
    box-shadow:
      0 0 0 2px rgba(168,85,247,0.7),
      0 0 0 6px rgba(168,85,247,0.18),
      0 0 28px 4px rgba(168,85,247,0.25);
  }
  50% {
    box-shadow:
      0 0 0 3px rgba(168,85,247,0.95),
      0 0 0 8px rgba(168,85,247,0.28),
      0 0 36px 8px rgba(168,85,247,0.4);
  }
}
@keyframes lili-tour-card-in {
  from { opacity: 0; transform: scale(0.96) translateY(4px); }
  to   { opacity: 1; transform: scale(1)    translateY(0);   }
}
`;

export function CoachmarkTour() {
  const status = useTourStore(s => s.status);
  const currentStepIndex = useTourStore(s => s.currentStepIndex);
  const next = useTourStore(s => s.next);
  const prev = useTourStore(s => s.prev);
  const skip = useTourStore(s => s.skip);
  const finish = useTourStore(s => s.finish);

  const { isMobile } = useViewport();

  const step: TourStep | undefined = tourSteps[currentStepIndex];
  /** On mobile, sidebar-anchored steps spotlight the always-visible
      hamburger menu instead of an off-screen drawer item. */
  const effectiveSelector =
    isMobile && step?.mobileTargetSelector ? step.mobileTargetSelector : step?.targetSelector;
  const effectiveBody = isMobile && step?.mobileBody ? step.mobileBody : step?.body;
  const rect = useTargetRect(effectiveSelector);

  const popoverRef = useRef<HTMLDivElement | null>(null);
  const [popoverHeight, setPopoverHeight] = useState<number>(POPOVER_HEIGHT_FALLBACK);

  // Measure the popover after every step change so positioning uses the real height.
  useLayoutEffect(() => {
    if (!popoverRef.current) return;
    const h = popoverRef.current.getBoundingClientRect().height;
    if (h && Math.abs(h - popoverHeight) > 1) {
      setPopoverHeight(h);
    }
  }, [currentStepIndex, status, popoverHeight]);

  useEffect(() => {
    if (status !== 'active' || !effectiveSelector) return;
    let raf = 0;
    const probe = window.setTimeout(() => {
      if (document.querySelector(effectiveSelector)) return;
      // Give one extra frame so a slow lazy-load / route transition doesn't
      // cause a step to be silently skipped before its target mounts.
      raf = window.requestAnimationFrame(() => {
        if (!document.querySelector(effectiveSelector)) {
          next(tourSteps.length);
        }
      });
    }, 600);
    return () => {
      window.clearTimeout(probe);
      if (raf) window.cancelAnimationFrame(raf);
    };
  }, [status, effectiveSelector, next]);

  const handleNext = useCallback(() => {
    if (currentStepIndex >= tourSteps.length - 1) {
      finish();
    } else {
      next(tourSteps.length);
    }
  }, [currentStepIndex, finish, next]);

  useEffect(() => {
    if (status !== 'active') return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') skip();
      if (e.key === 'ArrowRight') handleNext();
      if (e.key === 'ArrowLeft') prev();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [status, skip, prev, handleNext]);

  // Mark <html> while the tour runs so the global CSS reset (below) can
  // neutralise backdrop-filter on competing stacking contexts.
  useEffect(() => {
    if (status !== 'active') return;
    document.documentElement.setAttribute('data-tour-active', 'true');
    return () => {
      document.documentElement.removeAttribute('data-tour-active');
    };
  }, [status]);

  if (status !== 'active' || !step) return null;

  const pos = getPopoverPosition(step, rect, popoverHeight);
  const isLast = currentStepIndex >= tourSteps.length - 1;
  const isFirst = currentStepIndex === 0;
  const totalSteps = tourSteps.length;

  return createPortal(
    <div
      data-tour-overlay
      className="fixed inset-0 z-[9999]"
      aria-live="polite"
      style={{ isolation: 'isolate' }}
    >
      <style>{TOUR_KEYFRAMES}</style>
      <style>{`
        /* While the tour is active, neutralise backdrop-filter on the
           sidebar/topbar AND their descendants so the spotlight overlay
           can sit above every stacking context they spawn. */
        html[data-tour-active="true"] aside,
        html[data-tour-active="true"] aside *,
        html[data-tour-active="true"] aside::before,
        html[data-tour-active="true"] header,
        html[data-tour-active="true"] header * {
          backdrop-filter: none !important;
          -webkit-backdrop-filter: none !important;
        }
      `}</style>

      {rect && step.placement !== 'center' ? (
        <>
          {/* SVG with a foreignObject that hosts a backdrop-blurred + dim div,
              masked to a rounded-rect hole at the target. Using foreignObject
              keeps SVG masking (with rx/ry) while letting CSS backdrop-filter
              apply to all underlying app chrome (topbar, sidebar, content). */}
          <svg
            role="presentation"
            onClick={skip}
            aria-hidden
            width={window.innerWidth}
            height={window.innerHeight}
            viewBox={`0 0 ${window.innerWidth} ${window.innerHeight}`}
            className="absolute inset-0 z-[100] cursor-default"
          >
            <defs>
              <mask id="lili-tour-spotlight">
                <rect
                  x="0"
                  y="0"
                  width={window.innerWidth}
                  height={window.innerHeight}
                  fill="white"
                />
                <rect
                  x={Math.max(0, rect.left - SPOTLIGHT_PAD)}
                  y={Math.max(0, rect.top - SPOTLIGHT_PAD)}
                  width={rect.width + SPOTLIGHT_PAD * 2}
                  height={rect.height + SPOTLIGHT_PAD * 2}
                  rx={12}
                  ry={12}
                  fill="black"
                />
              </mask>
            </defs>
            <foreignObject
              x="0"
              y="0"
              width={window.innerWidth}
              height={window.innerHeight}
              mask="url(#lili-tour-spotlight)"
            >
              <div
                // @ts-expect-error xmlns is required for foreignObject HTML
                xmlns="http://www.w3.org/1999/xhtml"
                style={{
                  width: '100%',
                  height: '100%',
                  backdropFilter: 'blur(10px) saturate(120%)',
                  WebkitBackdropFilter: 'blur(10px) saturate(120%)',
                  backgroundColor: 'rgba(17,15,35,0.55)',
                }}
              />
            </foreignObject>
          </svg>
          {/* Soft pulsing purple ring around the visible target.
              No position transition: scrolling already drives many small
              rect updates, so animating top/left/width/height makes the
              ring lag behind the target. */}
          <div
            aria-hidden
            className="absolute z-[101] rounded-xl pointer-events-none"
            style={{
              top: rect.top - SPOTLIGHT_PAD,
              left: rect.left - SPOTLIGHT_PAD,
              width: rect.width + SPOTLIGHT_PAD * 2,
              height: rect.height + SPOTLIGHT_PAD * 2,
              animation: 'lili-tour-pulse 2.4s ease-in-out infinite',
            }}
          />
        </>
      ) : (
        <div
          role="presentation"
          onClick={skip}
          className="absolute inset-0 z-[100] cursor-default"
          style={{
            backdropFilter: 'blur(10px) saturate(120%)',
            WebkitBackdropFilter: 'blur(10px) saturate(120%)',
            backgroundColor: 'rgba(17,15,35,0.55)',
          }}
        />
      )}

      <div
        ref={popoverRef}
        role="dialog"
        aria-label={step.title}
        onClick={e => e.stopPropagation()}
        className="absolute z-[102] bg-white rounded-2xl ring-1 ring-purple-100/80 overflow-hidden"
        style={{
          ...(isMobile && !pos.centered
            ? {
                // Bottom-sheet on mobile: full-width minus gutter, pinned
                // to the bottom. No arrow — the spotlight communicates the
                // target on its own and arrow geometry never lines up
                // cleanly with a sheet that spans the whole viewport.
                left: VIEWPORT_PADDING,
                right: VIEWPORT_PADDING,
                bottom: MOBILE_BOTTOM_OFFSET,
                width: 'auto',
              }
            : pos.centered
              ? {
                  top: '50%',
                  left: '50%',
                  transform: 'translate(-50%, -50%)',
                  // Clamp on narrow phones so the welcome modal never overflows
                  // the viewport (340 + 2×16 gutter ≈ 372px floor otherwise).
                  width: `min(${POPOVER_WIDTH}px, calc(100vw - ${VIEWPORT_PADDING * 2}px))`,
                }
              : {
                  top: pos.top,
                  left: pos.left,
                  width: POPOVER_WIDTH,
                }),
          boxShadow:
            '0 24px 60px -16px rgba(124,58,237,0.35), 0 10px 24px -8px rgba(15,23,42,0.18)',
          animation: 'lili-tour-card-in 200ms cubic-bezier(0.2, 0.8, 0.2, 1)',
        }}
      >
        <div className="absolute inset-x-0 top-0 h-1 bg-gradient-to-r from-purple-500 via-fuchsia-500 to-pink-500" />

        <div className="p-5">
          <div className="flex items-start justify-between gap-3 mb-3">
            <div className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-purple-50 text-purple-700 text-[11px] font-medium ring-1 ring-purple-100">
              <Sparkles className="size-3" />
              Step {currentStepIndex + 1} of {totalSteps}
            </div>
            <button
              type="button"
              onClick={skip}
              aria-label="Skip tour"
              className="text-gray-400 hover:text-gray-700 -mr-1 -mt-1 p-1 rounded-md hover:bg-gray-100 transition-colors"
            >
              <X className="size-4" />
            </button>
          </div>

          <h3 className="text-base font-semibold text-gray-900 tracking-tight mb-1.5">
            {step.title}
          </h3>
          <p className="text-sm text-gray-600 leading-relaxed">{effectiveBody}</p>

          <div className="mt-5 flex items-center justify-between gap-3">
            <div className="flex items-center gap-1.5" aria-hidden>
              {tourSteps.map((_, i) => (
                <span
                  key={i}
                  className={
                    i === currentStepIndex
                      ? 'h-1.5 w-5 rounded-full bg-gradient-to-r from-purple-500 to-pink-500 transition-all'
                      : i < currentStepIndex
                        ? 'h-1.5 w-1.5 rounded-full bg-purple-300 transition-all'
                        : 'h-1.5 w-1.5 rounded-full bg-gray-200 transition-all'
                  }
                />
              ))}
            </div>
            <div className="flex items-center gap-1.5">
              {!isFirst && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={prev}
                  className="text-gray-600 hover:text-gray-900 hover:bg-gray-100 h-9 px-3"
                >
                  <ArrowLeft className="size-3.5 mr-1" />
                  Back
                </Button>
              )}
              <Button
                size="sm"
                onClick={handleNext}
                className={
                  isLast
                    ? 'h-9 px-5 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white shadow-sm shadow-purple-500/30 font-medium'
                    : 'h-9 px-4 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white shadow-sm shadow-purple-500/30 font-medium'
                }
              >
                {isLast ? 'Got it' : 'Next'}
                {!isLast && <ArrowRight className="size-3.5 ml-1" />}
              </Button>
            </div>
          </div>
        </div>

        {!isMobile &&
          !pos.centered &&
          pos.placement &&
          rect &&
          (() => {
            const geom = getArrowGeometry(
              pos.placement,
              rect,
              { top: pos.top, left: pos.left },
              popoverHeight
            );
            return (
              <div
                aria-hidden
                className="absolute bg-white pointer-events-none"
                style={{
                  width: geom.width,
                  height: geom.height,
                  clipPath: geom.clipPath,
                  filter: 'drop-shadow(0 4px 8px rgba(124,58,237,0.2))',
                  ...geom.style,
                }}
              />
            );
          })()}
      </div>

      {!pos.centered && !isMobile && (
        <button
          type="button"
          onClick={skip}
          className="absolute z-[102] bottom-6 left-1/2 -translate-x-1/2 text-xs text-white/70 hover:text-white underline-offset-4 hover:underline transition-colors"
        >
          Skip tour
        </button>
      )}
    </div>,
    document.body
  );
}
